import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../App.css";
import { ApiBaseUrl } from "../env";

const recommendations = {
    "Strategie & Führung": {
        "🟢 Hoch": "Ihre Führung ist strategisch auf KI ausgerichtet – weiter so!",
        "🟡 Mittel": "Es bestehen gute Ansätze, jedoch sollten Vision und Leadership geschärft werden.",
        "🔴 Niedrig": "Beginnen Sie mit der Entwicklung einer klaren KI-Strategie auf Führungsebene."
    },
    "Technologie & Infrastruktur": {
        "🟢 Hoch": "Ihre technische Basis ist hervorragend vorbereitet für KI.",
        "🟡 Mittel": "Modernisierungspotenziale vorhanden – prüfen Sie Ihre Architektur.",
        "🔴 Niedrig": "Bauen Sie eine skalierbare und cloudfähige Infrastruktur auf."
    },
    "Datenverfügbarkeit & Datenqualität": {
        "🟢 Hoch": "Sie verfügen über eine solide Datenbasis mit guter Qualität.",
        "🟡 Mittel": "Einige Datenquellen sind gut, andere weniger – prüfen Sie den Datenfluss.",
        "🔴 Niedrig": "Datenzugriff und -qualität sollten strukturell verbessert werden."
    },
    "Mitarbeiterkompetenz & Kultur": {
        "🟢 Hoch": "Ihr Team ist offen für KI und gut geschult.",
        "🟡 Mittel": "Das Mindset ist vorhanden – gezielte Weiterbildungen können helfen.",
        "🔴 Niedrig": "Fördern Sie den kulturellen Wandel und bieten Sie erste KI-Schulungen an."
    },
    "Prozesse & Use-Case-Potenzial": {
        "🟢 Hoch": "Sie haben klare KI-Anwendungsfälle erkannt – sehr gut!",
        "🟡 Mittel": "Es gibt Potenzial – analysieren Sie Ihre Prozesse gezielter.",
        "🔴 Niedrig": "Identifizieren Sie Bereiche mit wiederholbaren Aufgaben als erste KI-Ansätze."
    }
};

export default function SwipeYourAI() {
    const [questionsData, setQuestionsData] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [responses, setResponses] = useState([]);
    const [finished, setFinished] = useState(false);
    const [results, setResults] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formData, setFormData] = useState({ firstName: "", lastName: "", company: "", email: "" });
    const [startTime, setStartTime] = useState(null);
    const [lastSwipeTime, setLastSwipeTime] = useState(null);
    const [showIntro, setShowIntro] = useState(true);

    // Reset all quiz state and show intro
    const resetCheck = () => {
        setCurrentIndex(0);
        setResponses([]);
        setFinished(false);
        setResults(null);
        setFormSubmitted(false);
        setFormData({ firstName: "", lastName: "", company: "", email: "" });
        setStartTime(null);
        setLastSwipeTime(null);
        setShowIntro(true);
    };

    useEffect(() => {
        fetch("/questions.json")
            .then((res) => res.json())
            .then((data) => setQuestionsData(data))
            .catch((err) => console.error("Fehler beim Laden der Fragen:", err));
    }, []);

    useEffect(() => {
        if (!showIntro && startTime === null) {
            const now = Date.now();
            setStartTime(now);
            setLastSwipeTime(now);
        }
        if (!showIntro) {
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 50);
        }
    }, [showIntro]);

    const [xIconOpacity, setXIconOpacity] = useState(0);
    const [heartIconOpacity, setHeartIconOpacity] = useState(0);
    const [xOffset, setXOffset] = useState(0);

    const calculateResults = (responses) => {
        const topicScores = {};

        responses.forEach(({ topic, response }) => {
            if (!topicScores[topic]) {
                topicScores[topic] = { score: 0, total: 0 };
            }
            topicScores[topic].score += response;
            topicScores[topic].total += 1;
        });

        const summary = Object.entries(topicScores).map(([topic, { score, total }]) => {
            const percent = Math.round((score / total) * 100);
            let level = "🔴 Niedrig";
            if (percent >= 67) level = "🟢 Hoch";
            else if (percent >= 34) level = "🟡 Mittel";

            return { topic, score, total, percent, level };
        });

        const overallScore = responses.reduce((sum, r) => sum + r.response, 0);
        const overallPercent = Math.round((overallScore / responses.length) * 100);
        let overallLevel = "🔴 Niedrig";
        if (overallPercent >= 67) overallLevel = "🟢 Hoch";
        else if (overallPercent >= 34) overallLevel = "🟡 Mittel";

        return { summary, overall: { percent: overallPercent, level: overallLevel } };
    };

    const handleSwipe = (value) => {
        const now = Date.now();
        const currentQuestion = questionsData.questions[currentIndex];


        const updatedResponses = [
            ...responses,
            {
                id: currentQuestion.id,
                topic: currentQuestion.topic,
                response: value,
                duration: now - lastSwipeTime,
            },
        ];
        setXOffset(0);
        setResponses(updatedResponses);
        setLastSwipeTime(now);

        if (currentIndex + 1 < questionsData.questions.length) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setFinished(true);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const resultData = calculateResults(responses);
        setResults(resultData);
        setFormSubmitted(true);

        await fetch(ApiBaseUrl + "/save-response", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                ...formData,
                responses,
                results: resultData,
                duration: Date.now() - startTime
            })
        });
    };

    const backgroundImage = questionsData?.background || "";
    const currentQuestion = questionsData?.questions[currentIndex];

    if (!questionsData) {
        return <div className="fullscreen">Fragen werden geladen...</div>;
    }

    if (showIntro) {
        return (
            <div>
                <div className="container h-full flex flex-col px-2 py-4 sm:p-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                        <div className="flex justify-center mb-4 sm:mb-0">
                            <img src="img/pluswerk/plux.png" alt="Pluwswerk Brzzzl" className="w-32 sm:w-64" />
                        </div>
                        <div>
                            <h1 className="mb-4 text-center sm:text-left break-words">
                                Wie bereit ist Ihr Unternehmen für den Einsatz von Künstlicher Intelligenz?
                            </h1>
                            <p className="leading-[150%] text-clamp-p mb-4 break-words">
                                Mit unserem interaktiven Quick-Check finden Sie es in wenigen Minuten heraus – ganz intuitiv per Swipe: <br />
                                <strong>rechts für Zustimmung, links für Ablehnung.</strong><br />
                                Am Ende erhalten Sie eine erste Einschätzung Ihrer KI-Readiness – fundiert, visuell und direkt nutzbar.
                            </p>
                            <button
                                onClick={() => setShowIntro(false)}
                                className="btn px-6 py-2 hover:bg-[#d4ff4d] w-full sm:w-auto"
                            >
                                Jetzt starten
                            </button>
                        </div>
                    </div>
                    <div className="relative flex items-center justify-center footer text-center">
                        <p className="mt-4">Ein Projekt von <a href="http://www.pluswerk.digital" target="_blank">+Pluswerk</a> | <a href="https://www.pluswerk.digital/impressum" target="_blank">Impressum</a> | <a href="https://www.pluswerk.digital/rechtliche-hinweise-datenschutz" target="_blank">Datenschutz</a></p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Abort/Reset button: only show during quiz (not intro, not finished) */}
            {!showIntro && (
                <button onClick={resetCheck} className="absolute top-4 right-4 px-6 py-2 sm:w-auto reset-btn" style={{zIndex:1}} tabIndex="-1" aria-label="zurück zum Anfang">
                    <img src="/img/arrow-clockwise.svg" alt="Reset" className="w-6 h-6" />
                </button>
            )}
            {/* ...existing code... */}
            {!finished ? (
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentQuestion.id}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ x: xOffset * 2, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fullscreen"
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        onDragEnd={(e, info) => {
                            if (info.offset.x > 100) {
                                handleSwipe(1);
                            } else if (info.offset.x < -100) {
                                handleSwipe(0);
                            }
                            // reset icon opacities after swipe
                            setXIconOpacity(0);
                            setHeartIconOpacity(0);
                        }}
                        // Increase opacity for x-icon (swipe left) and heart-icon (swipe right)
                        onDrag={(e, info) => {
                            const xOffset = info.offset.x;
                            setXIconOpacity(Math.max(0, Math.min(1, -xOffset / 100)));
                            setHeartIconOpacity(Math.max(0, Math.min(1, xOffset / 100)));
                            setXOffset(info.offset.x);
                        }}
                    >
                        <div
                            className="container flex flex-col justify-between max-w-full break-words"
                            style={{
                                backgroundImage: "radial-gradient(closest-side at 50% 40%,rgba(255,255,255,0.5), transparent, transparent)",
                                height: '100svh',
                                minHeight: '100svh',
                                maxHeight: '100svh',
                                margin: 0,
                                padding: 0,
                                overflow: 'hidden',
                                boxSizing: 'border-box',
                                touchAction: 'pan-x pan-y',
                                overscrollBehavior: 'none'
                            }}
                        >
                            <div className="topic">
                                <h1 className="text-2xl sm:text-4xl text-center font-headline mt-10 mb-2 sm:mt-10 sm:mb-2">
                                    {currentQuestion.topic}
                                </h1>
                            </div>
                            <div className="relative flex items-center justify-center flex-grow" style={{minHeight:0}}>
                                <div className="x-icon absolute left-4 sm:left-16 top-1/2 -translate-y-1/2">
                                    <button style={{ opacity: xIconOpacity, transform: `scale(${xIconOpacity})` }} className="px-6 py-2 rounded-xl shadow w-full sm:w-auto" tabIndex="-1">nope</button>
                                </div>
                                <div
                                    className="plux mx-2 sm:mx-8 w-[350px] max-w-[80vw] h-[60vh] max-h-[60vh] min-h-[30vh] bg-center bg-no-repeat bg-contain"
                                    style={{ backgroundImage: `url(${currentQuestion.background})` }}
                                ></div>
                                <div className="heart-icon absolute right-4 sm:right-16 top-1/2 -translate-y-1/2">
                                    <button style={{ opacity: heartIconOpacity, transform: `scale(${heartIconOpacity})` }} className="px-6 py-2 rounded-xl shadow w-full sm:w-auto" tabIndex="-1">stimme zu</button>
                                </div>
                            </div>
                            <div className="question" style={{flexGrow:0}}>
                                <div className="text-center text-base sm:text-xl font-body max-w-full p-2 sm:p-1 pb-0 mb-1">
                                    <p className="text-xl sm:text-3xl font-body p-2 sm:p-3">
                                        {currentQuestion.text}
                                    </p>
                                </div>
                            </div>
                            <div className="flex justify-evenly items-center pb-8">
                                <button onClick={() => handleSwipe(0)} aria-label="Ablehnen">
                                    <img alt="X Icon" className="w-8 sm:w-12" src="/img/x-circle.svg" />
                                </button>
                                <img alt="Lobby Logo" className="w-24 sm:w-44" src="/img/pluswerk/logo_pluswerk_landscape_blue-black.png" />
                                <button onClick={() => handleSwipe(1)} aria-label="Zustimmen">
                                    <img alt="Heart Icon" className="w-8 sm:w-12" src="/img/heart.svg" />
                                </button>
                            </div>
                            <div className="relative flex items-center justify-center footer text-center">
                                <p className="mt-4">Ein Projekt von <a href="http://www.pluswerk.digital" target="_blank">+Pluswerk</a> | <a href="https://www.pluswerk.digital/impressum" target="_blank">Impressum</a> | <a href="https://www.pluswerk.digital/rechtliche-hinweise-datenschutz" target="_blank">Datenschutz</a></p>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>
            ) : !formSubmitted ? (
                <div>
                <form onSubmit={handleFormSubmit} className="fullscreen flex flex-col justify-center items-center p-8">
                    <div className="max-w-xl w-full">
                        <h1 className="text-2xl sm:text-3xl text-center font-headline mt-10 mb-2 sm:mt-10 sm:mb-2">Fast geschafft – Ihre Angaben bitte:</h1>
                        <p className="mb-4">
                            Erhalten Sie <strong>direkt im Anschluss</strong> eine schnelle Auswertung – und zusätzlich per E-Mail eine <strong>ausführliche Bewertung</strong> Ihrer KI-Readiness.
                        </p>
                        <p className="text-sm mb-6">
                            Ihre Angaben werden <strong>vertraulich behandelt</strong> und ausschließlich zur Erstellung Ihrer individuellen Auswertung verwendet. <br />
                            Es erfolgt <strong>keine Weitergabe an Dritte</strong>.
                        </p>
                        <label htmlFor="fullName">Name *</label>
                        <input type="text" name="fullName" id="fullName" value={formData.fullName} onChange={handleFormChange} required autoComplete="off" className="mb-2 p-3" />
                        <label htmlFor="company">Firma *</label>
                        <input type="text" name="company" id="company" value={formData.company} onChange={handleFormChange} required autoComplete="off" className="mb-2 p-3 w-full rounded text-black" />
                        <label htmlFor="email">E-Mail-Adresse *</label>
                        <input type="email" name="email" id="email" value={formData.email} onChange={handleFormChange} required autoComplete="off" className="mb-4 p-3 w-full rounded text-black" />
                        <p className="mt-2 mb-2">
                            Mit dem Absenden erklären Sie sich mit unserer <a href="https://www.die-lobby.de/datenschutz" target="_blank" rel="noopener noreferrer" className="underline">Datenschutzerklärung</a> einverstanden.
                        </p>

                        <button type="submit" className="btn px-6 py-2 hover:bg-[#d4ff4d] w-full sm:w-auto">Absenden</button>
                    </div>
                </form>
                    <div className="relative flex items-center justify-center footer text-center">
                        <p className="mt-4">Ein Projekt von <a href="http://www.pluswerk.digital" target="_blank">+Pluswerk</a> | <a href="https://www.pluswerk.digital/impressum" target="_blank">Impressum</a> | <a href="https://www.pluswerk.digital/rechtliche-hinweise-datenschutz" target="_blank">Datenschutz</a></p>
                    </div>
                </div>
            ) : (
                <div className="container h-full flex flex-col p-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
                        <div className="flex justify-center mb-4 sm:mb-0">
                            <img src="img/pluswerk/plux-wizard.png" alt="Plux Wizard" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl text-center font-headline mt-10 mb-2 sm:mt-10 sm:mb-2">Ihre Auswertung</h1>
                            <ul className="mt-4 mb-4">
                                {results.summary.map((r) => (
                                    <li key={r.topic} className="mb-4">
                                        <strong>{r.topic}:</strong> {r.percent}% – {r.level}
                                        {recommendations[r.topic] && recommendations[r.topic][r.level] && (
                                            <p className="text-sm mt-1">
                                                {recommendations[r.topic][r.level]}
                                            </p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            <h2 className="text-2xl font-bold mt-6">Gesamtbewertung: {results.overall.percent}%
                                – {results.overall.level}</h2>
                            <p className="mt-4 mb-4">Die ausführliche Analyse erhalten Sie im Laufe des Tages
                                per E-Mail.</p>

                            <button
                                onClick={resetCheck}
                                className="btn px-6 py-2 hover:bg-[#d4ff4d] w-full sm:w-auto"
                            >
                                zurück zum Start
                            </button>
                        </div>
                    </div>
                    <div className="relative flex items-center justify-center footer text-center">
                        <p className="mt-4">Ein Projekt von <a href="http://www.pluswerk.digital" target="_blank">+Pluswerk</a> | <a href="https://www.pluswerk.digital/impressum" target="_blank">Impressum</a> | <a href="https://www.pluswerk.digital/rechtliche-hinweise-datenschutz" target="_blank">Datenschutz</a></p>
                    </div>
                </div>
            )}
        </div>
    );
}
